/**
 * Readings Page (FIXED)
 * Quick access page for recording meter readings
 */

import React, { useState, useEffect } from 'react';
import { MeterCardGrid } from '@components/meters/meter_card';
import { MeterReadingForm } from '@components/meters/meter_reading_form';
import { PageLoading } from '@components/common/loading_spinner';
import { Card } from '@components/common/card';
import { Input } from '@components/common/input';
import { Button } from '@components/common/button';
import { toast } from 'react-toastify';
import {
  Meter,
  CreateMeterReadingPayload
} from '@/types/meter_types';
import {
  fetch_meters,
  create_meter_reading
} from '@/services/meter_service';
import {
  Search,
  Filter,
  Activity,
  CheckCircle,
  AlertCircle
} from 'lucide-react';

export const ReadingsPage: React.FC = () => {
  // State management
  const [meters, set_meters] = useState<Meter[]>([]);
  const [is_loading, set_is_loading] = useState(true);
  const [is_reading_form_open, set_is_reading_form_open] = useState(false);
  const [is_submitting, set_is_submitting] = useState(false);
  const [selected_meter, set_selected_meter] = useState<Meter | null>(null);

  // Filters
  const [search_term, set_search_term] = useState('');
  const [filter_status, set_filter_status] = useState<'all' | 'needs_reading' | 'recent'>('all');

  // Load meters on mount
  useEffect(() => {
    load_meters();
  }, []);

  const load_meters = async () => {
    try {
      set_is_loading(true);
      const data = await fetch_meters();
      const meters_array = Array.isArray(data) ? data : [];
      const active_meters = meters_array.filter(m => (m as any)?.status === 'ACTIVE');
      
      set_meters(active_meters);
      
      if (active_meters.length === 0 && meters_array.length === 0) {
        toast.info('No meters found. Please check your API configuration.');
      }
    } catch (error) {
      console.error('Error loading meters:', error);
      toast.error('Failed to load meters');
      set_meters([]);
    } finally {
      set_is_loading(false);
    }
  };

  const needs_reading = (meter: Meter): boolean => {
    if (!meter?.last_reading_date) return true;
    
    try {
      const days_since = Math.floor(
        (Date.now() - new Date(meter.last_reading_date).getTime()) / (1000 * 60 * 60 * 24)
      );
      return days_since > 30;
    } catch {
      return true;
    }
  };

  const recently_read = (meter: Meter): boolean => {
    if (!meter?.last_reading_date) return false;
    
    try {
      const days_since = Math.floor(
        (Date.now() - new Date(meter.last_reading_date).getTime()) / (1000 * 60 * 60 * 24)
      );
      return days_since <= 7;
    } catch {
      return false;
    }
  };

  // Filter meters
  const filtered_meters = meters.filter(meter => {
    if (!meter) return false;
    
    // Apply search filter
    if (search_term) {
      const term = search_term.toLowerCase();
      const meter_number = meter.meter_number?.toLowerCase() || '';
      const client_name = meter.client_name?.toLowerCase() || '';
      const location = meter.location_description?.toLowerCase() || '';
      
      const matches = 
        meter_number.includes(term) ||
        client_name.includes(term) ||
        location.includes(term);
        
      if (!matches) return false;
    }

    // Apply status filter
    if (filter_status === 'needs_reading') {
      return needs_reading(meter);
    } else if (filter_status === 'recent') {
      return recently_read(meter);
    }

    return true;
  });

  const handle_add_reading = (meter: Meter) => {
    set_selected_meter(meter);
    set_is_reading_form_open(true);
  };

  /**
   * Handle meter reading submission - FIXED
   */
  const handle_reading_submit = async (data: CreateMeterReadingPayload) => {
    try {
      set_is_submitting(true);

      // Create FormData if photo is present
      let payload: FormData | CreateMeterReadingPayload = data;

      if (data.photo) {
        const form_data = new FormData();
        // FIXED: Use 'meter' and 'current_reading' to match API expectations
        form_data.append('meter', data.meter.toString());  // Changed from meter_id
        form_data.append('current_reading', data.current_reading.toString());  // Changed from reading_value
        form_data.append('reading_date', data.reading_date);
        if (data.notes) {
          form_data.append('notes', data.notes);
        }
        form_data.append('photo', data.photo);
        payload = form_data;
      }

      await create_meter_reading(payload);
      toast.success('Reading recorded successfully');
      set_is_reading_form_open(false);
      await load_meters();
    } catch (error) {
      console.error('Error saving reading:', error);
      toast.error('Failed to save meter reading');
    } finally {
      set_is_submitting(false);
    }
  };

  if (is_loading) {
    return <PageLoading message="Loading meters..." />;
  }

  // Calculate stats
  const total_active = meters.length;
  const needs_reading_count = meters.filter(needs_reading).length;
  const recently_read_count = meters.filter(recently_read).length;
  const completion_rate = total_active > 0 
    ? Math.round(((total_active - needs_reading_count) / total_active) * 100)
    : 0;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-charcoal">Meter Readings</h1>
          <p className="text-gray-600 mt-1">
            Record water meter readings quickly and efficiently
          </p>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card padding="md">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center">
              <Activity size={24} />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Active</p>
              <p className="text-2xl font-bold text-charcoal">{total_active}</p>
            </div>
          </div>
        </Card>

        <Card padding="md">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-warning bg-opacity-20 text-warning flex items-center justify-center">
              <AlertCircle size={24} />
            </div>
            <div>
              <p className="text-sm text-gray-500">Need Reading</p>
              <p className="text-2xl font-bold text-warning">{needs_reading_count}</p>
            </div>
          </div>
        </Card>

        <Card padding="md">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-success bg-opacity-20 text-success flex items-center justify-center">
              <CheckCircle size={24} />
            </div>
            <div>
              <p className="text-sm text-gray-500">Recently Read</p>
              <p className="text-2xl font-bold text-success">{recently_read_count}</p>
            </div>
          </div>
        </Card>

        <Card padding="md">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-purple-100 text-purple-600 flex items-center justify-center">
              <Activity size={24} />
            </div>
            <div>
              <p className="text-sm text-gray-500">Completion</p>
              <p className="text-2xl font-bold text-purple-600">{completion_rate}%</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
        <div className="flex-1 w-full sm:max-w-md">
          <Input
            type="text"
            placeholder="Search meters by number, client, location..."
            value={search_term}
            onChange={(e) => set_search_term(e.target.value)}
            icon_left={<Search size={18} />}
          />
        </div>

        <div className="flex gap-2">
          <Button
            variant={filter_status === 'needs_reading' ? 'primary' : 'outline'}
            size="md"
            onClick={() => set_filter_status('needs_reading')}
            icon_left={<AlertCircle size={18} />}
          >
            Need Reading ({needs_reading_count})
          </Button>
          <Button
            variant={filter_status === 'recent' ? 'primary' : 'outline'}
            size="md"
            onClick={() => set_filter_status('recent')}
            icon_left={<CheckCircle size={18} />}
          >
            Recent ({recently_read_count})
          </Button>
          <Button
            variant={filter_status === 'all' ? 'primary' : 'outline'}
            size="md"
            onClick={() => set_filter_status('all')}
            icon_left={<Filter size={18} />}
          >
            All ({total_active})
          </Button>
        </div>
      </div>

      {/* Info Banner */}
      {filter_status === 'needs_reading' && needs_reading_count > 0 && (
        <div className="p-4 bg-warning bg-opacity-10 border border-warning rounded-lg">
          <div className="flex items-start gap-3">
            <AlertCircle size={20} className="text-warning flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-warning mb-1">
                {needs_reading_count} meter{needs_reading_count > 1 ? 's' : ''} overdue for reading
              </p>
              <p className="text-xs text-warning">
                These meters haven't been read in over 30 days. Please prioritize recording their readings.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Empty State */}
      {filtered_meters.length === 0 && !is_loading && (
        <Card padding="sm">
          <div className="text-center py-12">
            <Activity size={48} className="mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold text-gray-700 mb-2">
              No meters found
            </h3>
            <p className="text-gray-500 mb-4">
              {search_term 
                ? 'Try adjusting your search terms or filters'
                : 'There are no active meters in the system'}
            </p>
            {search_term && (
              <Button
                variant="outline"
                size="md"
                onClick={() => {
                  set_search_term('');
                  set_filter_status('all');
                }}
              >
                Clear Filters
              </Button>
            )}
          </div>
        </Card>
      )}

      {/* Meters Grid */}
      {filtered_meters.length > 0 && (
        <MeterCardGrid
          meters={filtered_meters}
          on_add_reading={handle_add_reading}
          columns={3}
        />
      )}

      {/* Meter Reading Form */}
      <MeterReadingForm
        is_open={is_reading_form_open}
        on_close={() => set_is_reading_form_open(false)}
        on_submit={handle_reading_submit}
        meter={selected_meter}
        is_loading={is_submitting}
      />
    </div>
  );
};